import React, { useState, useEffect } from "react";

// const API_BASE = "https://hopeful-cosmic-koala.ngrok-free.app/api";
const API_BASE = " /api";

const Gallery = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [ministries, setMinistries]       = useState(["All"]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [searchTerm, setSearchTerm]       = useState("");
  const [selectedMinistry, setSelectedMinistry] = useState("All");
  const [showImageModal, setShowImageModal]     = useState(false);
  const [selectedImage, setSelectedImage]       = useState(null);
  const [currentIndex, setCurrentIndex]         = useState(0);

  // ── Fetch images ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchGallery = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE}/gallery/`, {
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "69420",
          },
        });
        if (!response.ok) throw new Error(`Failed to fetch gallery: ${response.status}`);
        const data = await response.json();
        const results = Array.isArray(data) ? data : (data.results ?? []);
        setGalleryImages(results);

        // Derive ministry filter tabs from results
        const uniqueMinistries = [
          "All",
          ...Array.from(
            new Set(
              results
                .filter((img) => img.ministry_work)
                .map((img) => img.ministry_work.title)
            )
          ),
          "Uncategorized",
        ];
        setMinistries(uniqueMinistries);
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  // ── Filter ────────────────────────────────────────────────────────────────
  const filteredImages = galleryImages.filter((img) => {
    const matchesSearch =
      (img.caption || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (img.ministry_work?.title || "").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesMinistry =
      selectedMinistry === "All" ||
      (selectedMinistry === "Uncategorized" && !img.ministry_work) ||
      (img.ministry_work && img.ministry_work.title === selectedMinistry);

    return matchesSearch && matchesMinistry;
  });

  // ── Lightbox helpers ──────────────────────────────────────────────────────
  const openImageModal = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    const newIndex =
      (currentIndex + direction + filteredImages.length) % filteredImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long", day: "numeric", year: "numeric",
    });
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (!showImageModal) return;
      if (e.key === "ArrowRight") navigateImage(1);
      if (e.key === "ArrowLeft")  navigateImage(-1);
      if (e.key === "Escape")     closeImageModal();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [showImageModal, currentIndex, filteredImages]);

  useEffect(() => {
    document.body.style.overflow = showImageModal ? "hidden" : "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [showImageModal]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css" />

      <div className="gallery-page">

        {/* HERO */}
        <section className="gallery-hero">
          <div className="hero-overlay">
            <div className="container">
              <div className="row align-items-center">
                <div className="col-lg-6 text-white mb-4 mb-lg-0">
                  <h1 className="display-3 fw-bold mb-3">Gallery</h1>
                  <p className="fs-5 mb-4">
                Behold the mercy from God & the fulfilment of His promises on earth!
                  </p>
                </div>
                <div className="col-lg-6">
                  <div className="search-container">
                    <div className="input-group input-group-lg shadow-lg">
                      <span className="input-group-text bg-white border-0">
                        <i className="bi bi-search text-primary fs-5"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-0"
                        placeholder="Search by caption or ministry..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FILTER TABS */}
        <section className="py-4 bg-white border-bottom sticky-top shadow-sm" style={{ zIndex: 100 }}>
          <div className="container">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="text-muted me-2 small">
                <i className="bi bi-funnel me-1"></i>Filter:
              </span>
              {ministries.map((ministry) => (
                <button
                  key={ministry}
                  className={`btn btn-sm rounded-pill px-3 ${
                    selectedMinistry === ministry ? "btn-primary" : "btn-outline-secondary"
                  }`}
                  onClick={() => setSelectedMinistry(ministry)}
                >
                  {ministry}
                </button>
              ))}
              <span className="ms-auto text-muted small">
                <i className="bi bi-images me-1"></i>
                {filteredImages.length} photo{filteredImages.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>
        </section>

        {/* GALLERY GRID */}
        <section className="py-5 bg-light">
          <div className="container">

            {/* Loading */}
            {loading && (
              <div className="d-flex justify-content-center align-items-center py-5">
                <div style={{ width: 44, height: 44, border: "4px solid #e8f4fd", borderTopColor: "#0b1c6b", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            {/* Error */}
            {!loading && error && (
              <div className="text-center py-5">
                <div style={{ fontSize: "3.5rem", marginBottom: 16 }}>⚠️</div>
                <h4 style={{ color: "#0b1c6b" }}>Could not load gallery</h4>
                <p className="text-muted">{error}</p>
              </div>
            )}

            {/* Content */}
            {!loading && !error && (
              filteredImages.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-images fs-1 text-muted mb-3 d-block"></i>
                  <h5 className="text-muted">No photos found</h5>
                  <p className="text-muted">Try adjusting your search or filter</p>
                </div>
              ) : (
                <div className="row g-3">
                  {filteredImages.map((img, index) => (
                    <div className="col-lg-3 col-md-4 col-6" key={img.id}>
                      <div
                        className="gallery-card position-relative overflow-hidden rounded-4"
                        onClick={() => openImageModal(img, index)}
                        style={{ cursor: "pointer", aspectRatio: "1 / 1" }}
                      >
                        <img
                          src={img.image}
                          alt={img.caption}
                          className="w-100 h-100 gallery-img"
                          style={{ objectFit: "cover" }}
                        />
                        {/* Hover overlay */}
                        <div className="gallery-overlay position-absolute inset-0 d-flex flex-column justify-content-end p-3">
                          {img.ministry_work && (
                            <span className="badge bg-primary bg-opacity-90 rounded-pill mb-2 align-self-start">
                              {img.ministry_work.title}
                            </span>
                          )}
                          <p className="text-white small fw-semibold mb-0 lh-sm">
                            {img.caption}
                          </p>
                          <p className="text-white-50 small mb-0">
                            <i className="bi bi-calendar3 me-1"></i>
                            {formatDate(img.created_at)}
                          </p>
                        </div>
                        {/* Zoom icon */}
                        <div className="zoom-icon position-absolute top-50 start-50 translate-middle">
                          <div
                            className="rounded-circle bg-white bg-opacity-90 d-flex align-items-center justify-content-center"
                            style={{ width: "48px", height: "48px" }}
                          >
                            <i className="bi bi-zoom-in text-primary fs-5"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </section>

        {/* LIGHTBOX */}
        {showImageModal && selectedImage && (
          <div
            className="modal fade show d-block lightbox-modal"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.92)" }}
            onClick={(e) => e.target === e.currentTarget && closeImageModal()}
          >
            <div className="modal-dialog modal-dialog-centered modal-xl">
              <div className="modal-content border-0 bg-transparent">

                {/* Top bar */}
                <div className="d-flex align-items-center justify-content-between px-3 pt-3 pb-2">
                  <div className="text-white">
                    {selectedImage.ministry_work && (
                      <span className="badge bg-primary rounded-pill me-2">
                        {selectedImage.ministry_work.title}
                      </span>
                    )}
                    <span className="small text-white-50">
                      {currentIndex + 1} / {filteredImages.length}
                    </span>
                  </div>
                  <button
                    className="btn btn-outline-light rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: "40px", height: "40px" }}
                    onClick={closeImageModal}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>

                {/* Image + navigation */}
                <div className="position-relative d-flex align-items-center justify-content-center px-5">
                  <button
                    className="btn btn-outline-light rounded-circle nav-btn position-absolute start-0 ms-2"
                    onClick={() => navigateImage(-1)}
                    title="Previous (← key)"
                  >
                    <i className="bi bi-chevron-left fs-5"></i>
                  </button>

                  <img
                    src={selectedImage.image}
                    alt={selectedImage.caption}
                    className="lightbox-image rounded-3"
                    style={{ maxHeight: "70vh", maxWidth: "100%", objectFit: "contain" }}
                  />

                  <button
                    className="btn btn-outline-light rounded-circle nav-btn position-absolute end-0 me-2"
                    onClick={() => navigateImage(1)}
                    title="Next (→ key)"
                  >
                    <i className="bi bi-chevron-right fs-5"></i>
                  </button>
                </div>

                {/* Caption bar */}
                <div className="text-center px-4 py-3">
                  {selectedImage.caption && (
                    <p className="text-white fw-semibold mb-1">{selectedImage.caption}</p>
                  )}
                  <p className="text-white-50 small mb-0">
                    <i className="bi bi-calendar3 me-1"></i>
                    {formatDate(selectedImage.created_at)}
                  </p>
                  <p className="text-white-50 small mt-2 mb-0">
                    <i className="bi bi-keyboard me-1"></i>
                    Use ← → arrow keys to navigate · Esc to close
                  </p>
                </div>

                {/* Thumbnail strip */}
                <div className="d-flex justify-content-center gap-2 pb-3 px-3 overflow-auto thumbnail-strip">
                  {filteredImages.map((img, idx) => (
                    <img
                      key={img.id}
                      src={img.image}
                      alt={img.caption}
                      onClick={() => { setCurrentIndex(idx); setSelectedImage(img); }}
                      className="rounded-2"
                      style={{
                        width: "60px", height: "60px",
                        objectFit: "cover",
                        cursor: "pointer",
                        flexShrink: 0,
                        opacity: idx === currentIndex ? 1 : 0.5,
                        border: idx === currentIndex ? "2px solid #159EEC" : "2px solid transparent",
                        transition: "all 0.2s",
                      }}
                    />
                  ))}
                </div>

              </div>
            </div>
          </div>
        )}

        {/* SOCIAL */}
        <section className="social-section py-5 text-center text-white">
          <div className="container py-4">
            <h2 className="display-5 fw-bold mb-3">Follow Us</h2>
            <p className="fs-6 mb-4 mx-auto" style={{ maxWidth: "600px" }}>
              Stay connected with Gospel City Church International through our social platforms.
            </p>
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <a href="https://www.linkedin.com/in/gospel-city-church-international-650a562a0/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-linkedin fs-4"></i>
                 </a>
                <a href="https://groups.imo.im/.5l9akqTOrYa5Tv4T4" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-chat-dots fs-4"></i>
                </a>
           
              <a href="https://x.com/GospelCityChur?t=x8dsFbFkghmYh02lV0FVxQ&s=03" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-twitter-x fs-4"></i>
              </a>
              <a href="https://whatsapp.com/channel/0029Vb769UyDzgTBAZZcb40F" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-whatsapp fs-4"></i>
              </a>
              <a href="https://youtube.com/@gospelcitychurchinternational?si=1pZx2ak4ss3b4HuU" target="_blank" rel="noopener noreferrer" className="btn btn-outline-light rounded-circle social-icon">
                <i className="bi bi-youtube fs-4"></i>
              </a>
            </div>
          </div>
        </section>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700;800&display=swap');
          * { font-family: 'Lexend', sans-serif; }

          .gallery-hero {
            position: relative;
            background-image: url('/images/image.png');
            background-size: cover;
            background-position: center;
            min-height: 500px;
            display: flex;
            align-items: center;
          }
          .hero-overlay { position: relative; width: 100%; z-index: 2; padding: 80px 0; }
          .gallery-hero::before {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(135deg, rgba(11,28,107,0.95), rgba(21,158,236,0.85));
            z-index: 1;
          }
          .search-container .form-control { padding: 14px 20px; font-size: 16px; }
          .search-container .form-control:focus { box-shadow: 0 0 0 0.25rem rgba(26,58,161,0.25); }
          .search-container .input-group-text { padding: 14px 20px; }

          .gallery-card { transition: transform 0.3s ease, box-shadow 0.3s ease; }
          .gallery-card:hover { transform: scale(1.03); box-shadow: 0 20px 50px rgba(11,28,107,0.25) !important; }
          .gallery-img { transition: transform 0.4s ease; }
          .gallery-card:hover .gallery-img { transform: scale(1.08); }
          .gallery-overlay {
            inset: 0;
            background: linear-gradient(to top, rgba(11,28,107,0.85) 0%, transparent 50%);
            opacity: 0;
            transition: opacity 0.3s ease;
          }
          .gallery-card:hover .gallery-overlay { opacity: 1; }
          .zoom-icon { opacity: 0; transition: opacity 0.3s ease; }
          .gallery-card:hover .zoom-icon { opacity: 1; }

          .lightbox-modal { animation: fadeIn 0.25s ease; }
          .lightbox-image { animation: zoomIn 0.3s ease; }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes zoomIn { from { transform: scale(0.92); opacity: 0; } to { transform: scale(1); opacity: 1; } }

          .nav-btn { width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; }
          .thumbnail-strip::-webkit-scrollbar { height: 4px; }
          .thumbnail-strip::-webkit-scrollbar-track { background: transparent; }
          .thumbnail-strip::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.3); border-radius: 2px; }

          .text-primary  { color: #1a3aa1 !important; }
          .bg-primary    { background-color: #0b1c6b !important; }
          .btn-primary   { background: #0b1c6b; border-color: #0b1c6b; }
          .btn-primary:hover { background: #159EEC; border-color: #159EEC; }
          .btn-outline-primary { color: #0b1c6b; border-color: #0b1c6b; }
          .btn-outline-primary:hover { background: #0b1c6b; border-color: #0b1c6b; color: #fff; }
          .social-section { background: linear-gradient(135deg, #0b1c6b, #159EEC); }
          .social-icon { width: 52px; height: 52px; display: flex; align-items: center; justify-content: center; transition: all 0.3s; }
          .social-icon:hover { background: rgba(255,255,255,0.3); transform: translateY(-4px) scale(1.1); }

          @media (max-width: 768px) {
            .gallery-hero { min-height: 400px; }
            .display-3 { font-size: 2.5rem; }
            .nav-btn { width: 36px; height: 36px; }
          }
        `}</style>
      </div>
    </>
  );
};

export default Gallery;